import { useId, useMemo, useRef, useState } from 'react';
import type { FieldMetadata, ObjectMetadata, RecordEvent, RecordRow } from '../../data/client';
import { useHealthConfigs, useRecordEvents } from '../../data/hooks';
import {
  EmptyState,
  ExpandableTextToggle,
  type ExpandableToggleState,
  GroupedForm,
  useIsDesktop,
} from '../../ui';
import { MergeHistoryCard } from '../identity/MergeHistoryCard';
import { RecordIdentityCard } from '../identity/RecordIdentityCard';
import { ChecklistHeaderMeta, ChecklistSection } from './ChecklistSection';
import { checklistFieldsOf, isChecklistGroup, newestWriterByField } from './checklist';
import {
  emptyFieldsLabel,
  type FieldGroup,
  type FieldPlacement,
  fieldGroups,
  foldEmptyFields,
  withoutFields,
} from './fieldGroups';
import { computedHealthFieldIds, HealthBlock, isHealthGroup } from './HealthBlock';
import { RecordFieldEditor } from './RecordFieldEditor';
import { RecordFieldRow } from './RecordFieldRow';
import { toggleKey, useLocalToggle } from './useLocalToggle';
import { useRecordFieldSave } from './useRecordFieldSave';

/**
 * The editable half of the record page: every field in position order, GROUPED, then
 * the system facts.
 *
 * It owns the two things a single row must not own - WHICH row is open (exactly one,
 * so a half-typed value cannot be left behind in three places) and the write outcome
 * per field, which has to survive the row closing.
 *
 * GROUPING IS THE POINT OF THIS CARD NOW (D8-D10). A twenty-seven field agency used to
 * render as twenty-seven identical rows; it renders as six collapsible blocks whose
 * order is the position of each group's first field. An object nobody has grouped
 * yields exactly one unlabelled group and therefore looks exactly as it did.
 */
export interface RecordDetailsProps {
  readonly object: ObjectMetadata;
  readonly fields: readonly FieldMetadata[];
  readonly record: RecordRow;
  /**
   * The phone's Overview segment shows the identity and THE FIRST SECTION
   * (`spec/screens/record.md` section 3 item 3); Details shows all of them. The
   * summary is the same component with one group, so the two segments cannot drift
   * apart in how a row looks or edits.
   */
  readonly firstSectionOnly?: boolean | undefined;
  /**
   * The field ids the identity band above this already shows (BR-RECORD, register A6-13:
   * header facts "never repeat below"; DECISION P5-W3-1 made it for the phone's Overview).
   * Only a caller that draws the band passes it - see `withoutFields` for why.
   */
  readonly hiddenFieldIds?: readonly string[] | undefined;
}

const NO_PINS: ReadonlyMap<string, FieldPlacement> = new Map();

export function RecordDetails({
  object,
  fields,
  record,
  firstSectionOnly = false,
  hiddenFieldIds,
}: RecordDetailsProps) {
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  /**
   * FIELD-TO-FIELD EDITING (dogfooding, 2026-08-18). Clicking row B while row A's
   * editor is open used to only close A: A commits on blur and collapses, every row
   * below moves up between mousedown and mouseup, and the click lands on the wrong
   * row. B's pointerdown records the intent HERE - a ref, deliberately not state,
   * because re-rendering mid-gesture would unmount the pressed button and blur A's
   * editor into an instant self-commit - and A's close consumes it.
   */
  const nextEditRef = useRef<string | null>(null);
  const closeEditor = () => {
    const next = nextEditRef.current;
    nextEditRef.current = null;
    setEditingFieldId(next);
  };
  const { save, markers, savingFieldId } = useRecordFieldSave(object.id, record.id);
  const groups = useMemo(
    () => withoutFields(fieldGroups(fields), hiddenFieldIds ?? []),
    [fields, hiddenFieldIds],
  );
  /**
   * D21: an archived record is READ-ONLY and the tombstone banner says so, so the rows
   * have to actually be read-only. It is threaded into every row of every group here
   * rather than read inside the row, because a grouping refactor that dropped it would
   * silently re-enable editing on archived records - the exact regression D4 names.
   */
  const readOnly = record.deletedAt !== null;
  const hasFields =
    groups.some((group) => group.fields.length > 0) || (hiddenFieldIds ?? []).length > 0;
  /**
   * D24: the newest refusal per field, so a locked field can show what was attempted and
   * offer the release beside it. Read from the SAME canonical key the timeline uses
   * (INV-13), so this is the request the page already makes rather than a second one.
   */
  const events = useRecordEvents(object.id, record.id);
  const refusals = useMemo(() => newestRefusalByField(events.data?.pages), [events.data?.pages]);
  /**
   * D3's source badge reads the SAME loaded pages the refusals do, so a checklist costs no
   * request of its own: the activity feed already asked for this record's timeline.
   */
  const writers = useMemo(() => newestWriterByField(events.data?.pages), [events.data?.pages]);
  /**
   * D17: which of this object's sections is the COMPUTED HEALTH BLOCK, answered by the rule
   * set rather than by a section name (INV-7).
   *
   * ONE FETCH FOR THE WHOLE PAGE, cached under one key by TanStack (INV-13), so this costs
   * one request per session rather than one per record. A record page on an object with no
   * rules gets `undefined` and renders exactly as it did before Phase B.
   */
  const healthConfigs = useHealthConfigs();
  const healthConfig = useMemo(
    () => healthConfigs.data?.find((row) => row.config.objectId === object.id)?.config,
    [healthConfigs.data, object.id],
  );
  /**
   * THE EMPTY FIELDS FOLD INTO ONE LINE (handbook DESIGN.md H4, N21; register A6-13), and two
   * kinds of field never fold because their emptiness is what they say: a checkpoint with no
   * date is a step not done (D3), and the health group's fields are the computed colour and the
   * judgement it asks for (D17). Both are read off configuration, never off a section name
   * (INV-7).
   */
  const keepOpen = useMemo(() => {
    const keep = new Set<string>();
    for (const group of groups) {
      if (isChecklistGroup(group.fields)) {
        for (const field of checklistFieldsOf(group.fields)) keep.add(field.id);
      }
      /* The whole health group: the computed fields say "no signal" when empty, and the block
         says "Give your judgment below", so the judgement fields stand below it, empty or not. */
      if (healthConfig !== undefined && isHealthGroup(healthConfig, group.fields)) {
        for (const field of group.fields) keep.add(field.id);
      }
    }
    return keep;
  }, [groups, healthConfig]);
  /**
   * A FIELD SOMEBODY IS WORKING ON STAYS WHERE IT STOOD (BR-RECORD, found by the integration
   * gate). Filling an empty field moved it out of the fold into its group, which remounted its
   * control: a file upload not yet confirmed was lost and the preview broke. So a field is
   * pinned to its place the moment work on it starts - its editor opens, its row is pressed, a
   * value is committed - and every folded field is pinned when the fold opens, because the fold
   * is where the work happens. The pins last for this record's visit.
   */
  const [pins, setPins] = useState<{
    readonly recordId: string;
    readonly placements: ReadonlyMap<string, FieldPlacement>;
  }>({ recordId: record.id, placements: new Map() });
  const pinned = pins.recordId === record.id ? pins.placements : NO_PINS;
  const pin = (ids: readonly string[], placement: FieldPlacement) =>
    setPins((current) => {
      const base = current.recordId === record.id ? current.placements : NO_PINS;
      const fresh = ids.filter((id) => !base.has(id));
      if (fresh.length === 0 && current.recordId === record.id) return current;
      const next = new Map(base);
      for (const id of fresh) next.set(id, placement);
      return { recordId: record.id, placements: next };
    });
  const { shown, folded, emptyCount } = useMemo(
    () => foldEmptyFields(groups, record.values, (field) => keepOpen.has(field.id), pinned),
    [groups, record.values, keepOpen, pinned],
  );

  /**
   * THE ROW EDITOR, LIFTED OUT OF `RecordFieldRow` FOR ONE CALLER (D4).
   *
   * A checklist row is not a `RecordFieldRow` - it is a circle, a label and a deadline - but
   * tapping its label opens the ORDINARY date editor, because a checkpoint is a date field
   * and this product has one way to type a day into one. The editor is the page's, so exactly
   * one row on the card is ever open, checklist or not.
   */
  const renderEditor = (field: FieldMetadata) => (
    <RecordFieldEditor
      objectId={object.id}
      recordId={record.id}
      field={field}
      value={record.values[field.id] ?? null}
      humanOwned={record.provenance[field.id] !== undefined}
      onCancel={closeEditor}
      onCommit={async (next) => {
        closeEditor();
        await save(field, next);
      }}
    />
  );

  /**
   * ONE FIELD, as the cell of the grid (wave 3 round 2). The row component is unchanged -
   * the same edit layer, the same status accessories, the same read-only rules; what the
   * grid changes is where it sits and whether its label is beside or above its value, both
   * of which are the stylesheet's business (`.record-grid` in index.css).
   */
  const renderRow = (field: FieldMetadata, placement: FieldPlacement) => (
    <RecordFieldRow
      key={field.id}
      objectId={object.id}
      recordId={record.id}
      field={field}
      value={record.values[field.id] ?? null}
      provenance={record.provenance[field.id]}
      refusal={refusals.get(field.id)}
      marker={markers[field.id]}
      isEditing={editingFieldId === field.id}
      isSaving={savingFieldId === field.id}
      readOnly={readOnly}
      onStartEdit={() => {
        pin([field.id], placement);
        nextEditRef.current = null;
        setEditingFieldId(field.id);
      }}
      onPreselect={() => {
        pin([field.id], placement);
        if (editingFieldId !== null && editingFieldId !== field.id) {
          nextEditRef.current = field.id;
        }
      }}
      onCancel={closeEditor}
      onCommit={async (next) => {
        pin([field.id], placement);
        // The row closes immediately and the optimistic value stands.
        // If the write is queued or refused, the marker says so.
        closeEditor();
        // The promise is AWAITED rather than dropped: a live control that needs to know
        // when its write landed reads it through the field context (`FilesInput`). The
        // ENDING it resolves with is the checklist's business alone, so it is swallowed
        // here rather than widening the field context's `onChange` to carry it.
        await save(field, next);
      }}
    />
  );

  /**
   * The phone's Overview segment carries the FIRST section only (section 3 item 3);
   * everywhere else this is every section the switch leaves standing.
   */
  const sections = firstSectionOnly ? shown.slice(0, 1) : shown;
  /**
   * EVERY SECTION OPENS (wave 3 round 2, R-B2). Round 1 opened sections in order until the
   * twelfth field and folded the rest, which was the right reading of section 1 while the
   * fields were a one-column stack in a 360 pane: forty rows in a column nobody can see the
   * end of. In the three-column grid a section of eight fields is three rows of cells, and
   * the owner's sentence is the acceptance test - "wenn ich auf Agency klicke will ich doch
   * so viel wie es geht auf einem Blick haben" (owner-37). What folds instead is the TAIL of
   * a long section, behind that section's own "Show more fields" (`FieldCells`), which is
   * where the spec puts progressive disclosure on this screen and what Apple's Calendar Info
   * window does with its own extra fields.
   * hig: https://developer.apple.com/design/human-interface-guidelines/disclosure-controls
   *
   * A section somebody FOLDS still stays folded: the default changed, the memory did not.
   */

  return (
    /*
      A NAMED REGION, NOT A CARD (section 7: "Apple does not put a details pane inside a
      bordered box within the content"). The heading is the region's accessible name and
      is not drawn - the toolbar already says which record this is, and System Settings
      draws no "Details" title over its first group either.
    */
    <section aria-label="Details" className="record-pane-section">
      <h2 className="sr-only">Details</h2>
      {!hasFields ? (
        <EmptyState
          icon="settings"
          title="No fields"
          description="Fields are defined in the admin area; this page is generated from them."
        />
      ) : (
        <>
          {sections.map((group) => {
            /**
             * D3: A GROUP OF DEADLINES IS A CHECKLIST, and the test is a capability rather
             * than a name. The remaining fields of such a group - there are none in the
             * seeded "Onboarding", and there may be some on an object an operator builds -
             * keep their ordinary rows underneath, because they are not checkpoints and a
             * circle they could never fill would be a lie about their state.
             */
            const checklist = isChecklistGroup(group.fields) ? checklistFieldsOf(group.fields) : [];
            /**
             * D17: AND A GROUP HOLDING THE COLOUR THE RULES WRITE IS THE HEALTH BLOCK,
             * decided by the CONFIG rather than by a section name (INV-7).
             *
             * The two tests are independent and they compose: a group could in principle be
             * both, and the order below is the order a reader needs - what is late, then
             * what the system concluded, then what is left to fill in.
             */
            const health =
              healthConfig !== undefined && isHealthGroup(healthConfig, group.fields)
                ? healthConfig
                : undefined;
            const computed =
              health === undefined ? new Set<string>() : computedHealthFieldIds(health);
            const rest = group.fields.filter(
              (field) => !checklist.includes(field) && !computed.has(field.id),
            );
            /**
             * The heading only appears where the fields under it ARE the judgement the block
             * asks for: an operator who puts something else in this section gets ordinary
             * rows with no invented title over them.
             */
            const assessment =
              health !== undefined && rest.some((field) => field.id === health.assessment.field);
            return (
              <FieldGroupBlock
                key={group.label ?? '__ungrouped__'}
                objectId={object.id}
                group={group}
                /* Open on arrival; a group somebody folded stays folded (see above). */
                defaultOpen
                meta={
                  checklist.length > 0 ? (
                    <ChecklistHeaderMeta fields={checklist} values={record.values} />
                  ) : undefined
                }
              >
                {checklist.length > 0 && (
                  <ChecklistSection
                    objectId={object.id}
                    record={record}
                    fields={checklist}
                    writers={writers}
                    readOnly={readOnly}
                    savingFieldId={savingFieldId}
                    onSave={save}
                    onEdit={(fieldId) => {
                      nextEditRef.current = null;
                      setEditingFieldId(fieldId);
                    }}
                    renderEditor={renderEditor}
                    editingFieldId={editingFieldId}
                  />
                )}
                {health !== undefined && <HealthBlock config={health} values={record.values} />}
                {assessment && (
                  /* WAVE 3 (R-B1): the kit's nested list header - Semibold at the row's own
                     size in the secondary label colour - and no capitals. A heading in
                     letter-spaced capitals has no master in either kit.
                     kit: Apple iOS 27 UI Kit.sketch / Lists/Light/Header/Nested */
                  <h4 className="record-group-subhead">Assessment</h4>
                )}
                {rest.length > 0 && (
                  <FieldCells
                    objectId={object.id}
                    groupLabel={group.label}
                    fields={rest}
                    renderRow={(field) => renderRow(field, 'open')}
                  />
                )}
              </FieldGroupBlock>
            );
          })}
          {/*
            THE FOLD STANDS AT THE END OF THE ROWS, as the checkbox it replaces did, and only
            where there is something in it. The phone's Overview leaves it out: that segment is
            the short summary, and Details one segment along carries the whole record.
          */}
          {/* While it holds a field: a field filled inside the open fold stays in it for the
              visit (`pin`), so the fold stays too, even when nothing in it is empty any more. */}
          {!firstSectionOnly && folded.length > 0 && (
            <EmptyFieldsFold
              groups={folded}
              count={emptyCount}
              renderRow={(field) => renderRow(field, 'folded')}
              onOpen={() =>
                pin(
                  folded.flatMap((group) => group.fields.map((field) => field.id)),
                  'folded',
                )
              }
            />
          )}
        </>
      )}

      {/*
        Below the fields, not beside them: identity answers "is this the right person"
        and a merge history answers "why does this record hold that", and both are
        questions somebody asks AFTER reading the values. Each renders nothing when it
        has nothing to say, so an object with no identifier fields is unchanged.
      */}
      {!firstSectionOnly && (
        <>
          <RecordIdentityCard object={object} recordId={record.id} />
          <MergeHistoryCard object={object} recordId={record.id} fields={fields} />
        </>
      )}
    </section>
  );
}

/**
 * THE FIELDS OF ONE SECTION, AS THE GRID AND ITS TAIL (wave 3 round 2).
 *
 * `spec/screens/record.md` section 2 item 6: "Rarely used fields of a section behind one
 * 'Show more fields' disclosure at the section's end, the way the Calendar Info window folds
 * its extra fields". Apple's own picture of that window carries the callout "Click to reveal
 * more fields" over a short default set.
 * hig: https://developer.apple.com/design/human-interface-guidelines/disclosure-controls
 * support: https://support.apple.com/guide/calendar/change-events-icl1018/mac
 *
 * DECISION P5-W3-9: WHICH FIELDS ARE THE RARE ONES, when the platform has no way to say so.
 * The brief asks for "the fields the admin marked as secondary"; no such flag exists on
 * `FieldMetadata` - `groupLabel`, `position`, `isHidden` and `isArchived` are the whole of
 * what an admin can say about where a field belongs - and inventing one is a metadata change,
 * which is not a design slice's to make. So the split is by POSITION, the one ordering an
 * admin really controls: the first eight fields of a section stand open, which is at most
 * three rows of the three-column grid, and the ninth onwards fold. An object nobody has
 * grouped has one section and therefore one disclosure, which is the same behaviour a
 * forty-field agency gets per section.
 */
const OPEN_FIELDS_PER_SECTION = 8;

function FieldCells({
  objectId,
  groupLabel,
  fields,
  renderRow,
}: {
  readonly objectId: string;
  readonly groupLabel: string | null;
  readonly fields: readonly FieldMetadata[];
  readonly renderRow: (field: FieldMetadata) => React.ReactNode;
}) {
  const [open, setOpen] = useLocalToggle(
    toggleKey('moreFields', objectId, groupLabel ?? ''),
    false,
  );
  const bodyId = useId();
  const shown = fields.slice(0, OPEN_FIELDS_PER_SECTION);
  const folded = fields.slice(OPEN_FIELDS_PER_SECTION);

  return (
    <>
      <div className="record-grid">{shown.map(renderRow)}</div>
      {folded.length > 0 && (
        <>
          {/* THE CONTROL PRECEDES THE REGION IT REVEALS, which is what `aria-controls` means
              and what keeps it in the same place whichever state it is in - a disclosure that
              jumps to the bottom of what it just opened is a control that moved under the
              pointer that pressed it.

              IT IS THE PRODUCT'S ONE "MORE" (BR-PARTS; DESIGN.md B1, D10): the library's
              expander toggle with the register's words, "More" (L17) and "Less" (R-W1), handed
              this section's own open state instead of a measured clamp. The row around it keeps
              the grouped row's inset and height, so the toggle stands where the old control
              stood. Its accessible name says which section it opens ("More, Identity"; the
              ungrouped section is the pane's "Details"), so it is not taken for a page's own
              "More" menu. */}
          <div className="record-row record-row-stacked">
            <ExpandableTextToggle
              state={{ expanded: open, setExpanded: setOpen, cut: true, bodyId }}
              subject={groupLabel ?? 'Details'}
            />
          </div>
          {/* THE WRAPPER CARRIES `hidden` AND THE GRID CARRIES `display: grid`. Put both on
              one element and the utility layer's `display: grid` beats the base layer's
              `[hidden] { display: none }`, so a folded section would render wide open. */}
          <div id={bodyId} hidden={!open}>
            <div className="record-grid">{folded.map(renderRow)}</div>
          </div>
        </>
      )}
    </>
  );
}

/**
 * THE EMPTY FIELDS, AS ONE LINE THAT OPENS THEM (BR-RECORD; handbook DESIGN.md H4: "fold empty
 * record fields into one line ("12 empty fields") that opens them", N21; register A6-13).
 *
 * The control precedes the region it reveals, so it stays where the pointer pressed it. Opened,
 * the empty fields stand under their own group's name in the same grid and edit the same way; a
 * field somebody fills there stays there for the rest of the visit (`pin`) and stands in its
 * group from the next visit on.
 * hig: https://developer.apple.com/design/human-interface-guidelines/disclosure-controls
 *
 * FOLDED ON EVERY VISIT. The fold is not remembered: "default folded" is the rule, and a fold
 * that stayed open for one record would stand open over twenty dashes on the next.
 */
function EmptyFieldsFold({
  groups,
  count,
  renderRow,
  onOpen,
}: {
  readonly groups: readonly FieldGroup[];
  readonly count: number;
  readonly renderRow: (field: FieldMetadata) => React.ReactNode;
  /** Opening the fold pins its fields, so filling one does not move it away (see `pin`). */
  readonly onOpen: () => void;
}) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  /**
   * THE PRODUCT'S ONE "MORE" (DESIGN.md D10, N9: "More" and "Less"; BR-PARTS made the library's
   * toggle the one expander): the toggle is handed this fold's own open state and the region it
   * reveals rather than a measured clamp, so the line says what is folded, "12 empty fields"
   * (H4), and the control beside it says More or Less like every other expander.
   */
  const toggle: ExpandableToggleState = {
    expanded: open,
    setExpanded: (next) => {
      if (next) onOpen();
      setOpen(next);
    },
    cut: true,
    bodyId,
  };
  /**
   * AND IT NAMES WHAT IT OPENS (coordinator, 4 Oct): "More" alone would be one of two on a record
   * whose long section has its own "More", so the toggle takes BR-PARTS' `subject` and is read as
   * "More, 12 empty fields". Handed as a spread, because the library on this branch predates the
   * prop; it takes effect where BR-PARTS' toggle is merged.
   */
  const toggleProps = { state: toggle, subject: emptyFieldsLabel(count) };
  return (
    <GroupedForm>
      <p className="record-row record-row-stacked text-sm text-ink-muted">
        <span>
          {emptyFieldsLabel(count)} <ExpandableTextToggle {...toggleProps} />
        </span>
      </p>
      <div id={bodyId} hidden={!open}>
        {groups.map((group) => (
          <div key={group.label ?? '__ungrouped__'}>
            {group.label !== null && <h4 className="record-group-subhead">{group.label}</h4>}
            <div className="record-grid">{group.fields.map(renderRow)}</div>
          </div>
        ))}
      </div>
    </GroupedForm>
  );
}

/**
 * One group, as the kit's Forms group box (P5-2). The UNGROUPED one has no title and
 * no disclosure: it has no name, so there is nothing for a chevron to be about, and
 * giving it a heading like "Other" would invent a group the admin never wrote.
 *
 * The open state is remembered per (object, group) - D10's recommendation, approved -
 * and the DEFAULT is now the section's position rather than "all open": the first is
 * open and the rest are named disclosures, which is what section 2 item 6 asks for and
 * what keeps a forty-field object from opening as forty rows.
 */
function FieldGroupBlock({
  objectId,
  group,
  meta,
  defaultOpen,
  children,
}: {
  readonly objectId: string;
  readonly group: FieldGroup;
  /**
   * D3's progress, in the disclosure HEADER rather than in the body it opens: a closed
   * checklist still has to say how far along it is and whether anything is late, which is
   * the whole reason the dots exist at that size.
   */
  readonly meta?: React.ReactNode | undefined;
  readonly defaultOpen: boolean;
  readonly children: React.ReactNode;
}) {
  const [open, setOpen] = useLocalToggle(
    toggleKey('fieldGroup', objectId, group.label ?? ''),
    defaultOpen,
  );
  /**
   * ON THE PHONE A SECTION IS NOT A DISCLOSURE (round 2, finding F5). iOS puts a PLAIN
   * header over each inset group and shows every group; the chevron on every section
   * that round 1 drew is a macOS Forms construction wearing an iOS list's clothes, and
   * `spec/screens/record.md` section 3 item 4 asks for "every section as inset grouped
   * lists with named headers" - named, not folded. The desktop inspector keeps the
   * disclosures, which is what section 2 item 6 asks for there.
   */
  const desktop = useIsDesktop();

  if (group.label === null) return <GroupedForm>{children}</GroupedForm>;

  if (!desktop) {
    return (
      <GroupedForm title={group.label} {...(meta === undefined ? {} : { actions: meta })}>
        {children}
      </GroupedForm>
    );
  }

  return (
    /**
     * NO COUNT ON A FIELD GROUP (audit P3-4). It carried `group.fields.length`, and a bare
     * number beside a word reads as DATA on a screen whose whole business is data: a CSM
     * reading "RISK 2" reads two risks, not two fields. It was never the record's number -
     * it was a count of this component's own rows.
     */
    <GroupedForm
      title={group.label}
      open={open}
      onOpenChange={setOpen}
      {...(meta === undefined ? {} : { actions: meta })}
    >
      {children}
    </GroupedForm>
  );
}

/**
 * The newest `write_blocked` event per field, over whatever pages of the timeline are
 * loaded. Events arrive newest first, so the first hit per field wins and no comparison
 * is needed - and a refusal older than the loaded window simply does not render, which is
 * the honest behaviour for a paged history.
 */
function newestRefusalByField(
  pages: readonly { readonly events: readonly RecordEvent[] }[] | undefined,
): ReadonlyMap<string, RecordEvent> {
  const found = new Map<string, RecordEvent>();
  for (const page of pages ?? []) {
    for (const event of page.events) {
      if (event.type !== 'write_blocked') continue;
      for (const entry of event.diff) {
        if (!found.has(entry.fieldId)) found.set(entry.fieldId, event);
      }
    }
  }
  return found;
}
